import PillButton from "client/common/components/buttons/PillButton";
import HorizontalOverflow from "client/common/components/display/HorizontalOverflow";
import { urls } from "client/routes";
import { useViewItemListOnVisible } from "client/analytics/utils";

import CategoryCard from "./CategoryCard";
import ProductLineCard, { ProductLineForCard } from "./ProductLineCard";
import { CarouselDisplayCategory, CarouselDisplayProductLine } from "../types";

export const toCardShape = (pl: CarouselDisplayProductLine): ProductLineForCard => ({
  ...pl.product_line,
  is_featured: pl.is_featured,
  is_loyalty_program_featured: pl.is_loyalty,
});

// Looked up by key from each caller's CSS module (which TS types as a string
// index signature), so we accept the same loose shape and read the keys we
// need at the call sites below.
export type CarouselSectionClassNames = Readonly<Record<string, string>>;

type TopSellerCarouselSectionProps = {
  productLines: CarouselDisplayProductLine[];
  viewName: string;
  classNames: CarouselSectionClassNames;
};

export const TopSellerCarouselSection = ({
  productLines,
  viewName,
  classNames,
}: TopSellerCarouselSectionProps) => {
  const topSellerCards = productLines.map(toCardShape);
  const ref = useViewItemListOnVisible(topSellerCards, `${viewName} - Featured`);

  return (
    <div className={classNames.category}>
      <div className={classNames.info}>
        <div className={classNames.titles}>
          <h3 className={classNames.title}>
            Browse <span className={classNames.accent}>popular</span> medications
          </h3>
        </div>
      </div>
      <HorizontalOverflow
        ref={ref}
        direction="right"
        classNames={{ scroller: classNames.products, button: classNames.button }}
      >
        <CategoryCard.TopSellers className={classNames.card} />
        {topSellerCards.map((productLine) => (
          <div key={productLine.id} className={classNames.card}>
            <ProductLineCard productLine={productLine} eventViewName={viewName} />
          </div>
        ))}
      </HorizontalOverflow>
    </div>
  );
};

type CategoryCarouselSectionProps = {
  category: CarouselDisplayCategory;
  productLines: CarouselDisplayProductLine[];
  viewName: string;
  classNames: CarouselSectionClassNames;
};

export const CategoryCarouselSection = ({
  category,
  productLines,
  viewName,
  classNames,
}: CategoryCarouselSectionProps) => {
  const categoryCarouselData = productLines.map(toCardShape);
  const ref = useViewItemListOnVisible(categoryCarouselData, `${viewName} - ${category.name}`);

  return (
    <div className={classNames.category}>
      <div className={classNames.info}>
        <div className={classNames.titles}>
          <h3 className={classNames.title}>{category.extended_name}</h3>
        </div>
        <PillButton
          size="s"
          variant="secondary"
          dest={urls.products.category({ params: { id: category.id } })}
        >
          View all
        </PillButton>
      </div>
      <HorizontalOverflow
        ref={ref}
        direction="right"
        classNames={{ scroller: classNames.products, button: classNames.button }}
      >
        <CategoryCard category={category} className={classNames.card} />
        {categoryCarouselData.map((productLine) => (
          <div key={productLine.id} className={classNames.card}>
            <ProductLineCard productLine={productLine} eventViewName={viewName} />
          </div>
        ))}
      </HorizontalOverflow>
    </div>
  );
};
